import React, { useState, useEffect } from "react";
import BasicInfo from "./components/BasicInfo";
import Stepper from "./components/Stepper";
import WeeklyDetails from "./components/WeeklyDetails";
import Review from "./components/Review";
import FooterBtn from "./components/FooterBtn";
import Title from "./components/Title";
function App() {
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [numberOfClients, setNumberOfClients] = useState<number>(0);
  const [numberOfWeeks, setNumberOfWeeks] = useState<number>(0);
  const [step, setStep] = useState<number>(1);
  const [weeksData, setWeeksData] = useState<
    { numberOfClients: number; accountValue: number; week: number }[]
  >([]);
  const [userName, setUserName] = useState<string | null>(null);
  const [stepTwoCompleted, setStepTwoCompleted] = useState<boolean>(false);

  const totalClients = weeksData.reduce(
    (sum, week) => sum + week.numberOfClients,
    0
  );
  const totalAccountValue = weeksData.reduce(
    (sum, week) => sum + week.accountValue,
    0
  );

  const isButtonDisabled =
    step === 1
      ? numberOfWeeks <= 0
      : step === 2
      ? totalClients <= 0 || totalAccountValue <= 0 || !stepTwoCompleted
      : false;

  const tooltipMessage =
    step === 2 && numberOfClients !== totalClients
      ? `The sum of clients across all weeks must equal ${numberOfClients}.`
      : "";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  useEffect(() => {
    const isValid = weeksData.every(({ numberOfClients, accountValue }) => {
      const bothZero = numberOfClients === 0 && accountValue === 0;
      const bothPositive = numberOfClients > 0 && accountValue > 0;
      return bothZero || bothPositive;
    });

    setStepTwoCompleted(isValid);
  }, [weeksData, setStepTwoCompleted]);

  const handleNext = () => {
    if (step === 1) {
      generateWeeksData(numberOfWeeks);
    }
    setStep((prev) => prev + 1);
  };

  const handlePrev = () => {
    setStep((prev) => prev - 1);
    setNumberOfClients(0);
    setNumberOfWeeks(0);
    setWeeksData([]);
  };

  const handleRestart = () => {
    setStep(1);
    setStartDate(new Date());
    setNumberOfClients(0);
    setNumberOfWeeks(0);
    setWeeksData([]);
  };

  const generateWeeksData = (weeks: number) => {
    setWeeksData((prevWeeksData) => {
      if (weeks > prevWeeksData.length) {
        return [
          ...prevWeeksData,
          ...Array.from(
            { length: weeks - prevWeeksData.length },
            (_, index) => ({
              numberOfClients: 0,
              accountValue: 0,
              week: prevWeeksData.length + index + 1,
            })
          ),
        ];
      } else {
        return prevWeeksData.slice(0, weeks);
      }
    });
  };

  const handleInputChangeInReview = (field: string, value: string) => {
    if (field === "numberOfWeeks") {
      if (value === "") {
        setNumberOfWeeks(0);
        generateWeeksData(0);
        return;
      }
    }

    if (/^\d*$/.test(value)) {
      const numericValue = Number(value);
      switch (field) {
        case "numberOfClients":
          setNumberOfClients(numericValue);
          break;
        case "numberOfWeeks":
          setNumberOfWeeks(numericValue);
          generateWeeksData(numericValue);
          break;
        default:
          break;
      }
    } else {
      setStartDate(new Date(value));
    }
  };

  const handleWeekChange = (
    weekIndex: number,
    field: string,
    value: number
  ) => {
    setWeeksData((prev) =>
      prev.map((week, index) =>
        index === weekIndex ? { ...week, [field]: value } : week
      )
    );
  };

  return (
    <div className="bg-white h-full">
      <main className="mb-20 py-8 h-full relative">
        <div
          className="w-full max-w-[1240px] px-8 mx-auto"
          style={{ padding: "2rem 0" }}
        >
          <div className="">
            <div className="relative">
              <Stepper currentStep={step} />
            </div>

            <div className="md:mt-0 w-full relative">
              <Title />

              <BasicInfo
                startDate={startDate}
                setStartDate={setStartDate}
                numberOfClients={numberOfClients}
                setNumberOfClients={setNumberOfClients}
                numberOfWeeks={numberOfWeeks}
                setNumberOfWeeks={setNumberOfWeeks}
                isActive={step === 1}
              />

              <WeeklyDetails
                weeksData={weeksData}
                handleWeekChange={handleWeekChange}
                numberOfClients={numberOfClients}
                totalClients={totalClients}
                totalAccountValue={totalAccountValue}
                isActive={step === 2}
              />

              <Review
                startDate={startDate}
                numberOfClients={numberOfClients}
                numberOfWeeks={numberOfWeeks}
                weeksData={weeksData}
                handleInputChange={handleInputChangeInReview}
                handleWeekChange={handleWeekChange}
                totalClients={totalClients}
                totalAccountValue={totalAccountValue}
                userName={userName}
                isActive={step === 3}
              />
            </div>
            {step !== 3 ? <div className="w-[100px]"></div> : null}
          </div>
        </div>
        <FooterBtn
          handlePrev={handlePrev}
          handleNext={handleNext}
          handleRestart={handleRestart}
          currentStep={step}
          isButtonDisabled={isButtonDisabled}
          tooltipMessage={tooltipMessage}
        />
      </main>
    </div>
  );
}

export default App;
